import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Clock,
  Plus,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  Receipt,
  Filter,
  Loader2,
  Ticket,
} from 'lucide-react';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';
import { usePatientAppointments, type AppointmentStatus } from '@/hooks/usePatientAppointments';
import { AppointmentModal } from '@/pages/appointments/AppointmentModal';
import { Button } from '@/components/ui/Button';

interface AppointmentTabProps {
  patientId: string;
  clinicId: string;
}

export function AppointmentTab({ patientId, clinicId }: AppointmentTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: appointments, isLoading, error } = usePatientAppointments(patientId);
  const [showNewModal, setShowNewModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<AppointmentStatus | 'all'>('all');

  const filtered =
    appointments?.filter((apt) => statusFilter === 'all' || apt.status === statusFilter) ?? [];

  const upcoming = filtered.filter((apt) => {
    const startDate = new Date(apt.starts_at);
    return apt.status === 'upcoming' && startDate >= new Date();
  });

  const past = filtered.filter((apt) => {
    const startDate = new Date(apt.starts_at);
    return (
      apt.status === 'completed' ||
      apt.status === 'cancelled' ||
      apt.status === 'no_show' ||
      (apt.status === 'upcoming' && startDate < new Date())
    );
  });

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return {
      date: format(date, 'dd. MMMM yyyy', { locale: da }),
      time: format(date, 'HH:mm'),
    };
  };

  const getStatusBadge = (status: AppointmentStatus) => {
    switch (status) {
      case 'upcoming':
        return (
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
            {t('appointments.status.upcoming')}
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-200">
            <CheckCircle className="mr-1 h-3 w-3" />
            {t('appointments.status.completed')}
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            <XCircle className="mr-1 h-3 w-3" />
            {t('appointments.status.cancelled')}
          </span>
        );
      case 'no_show':
        return (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            <AlertCircle className="mr-1 h-3 w-3" />
            {t('appointments.status.no_show')}
          </span>
        );
    }
  };

  const getPaymentBadge = (
    status: string | null,
    amountCents: number | null,
    currency: string | null,
  ) => {
    if (!status) {
      return (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
          {t('appointments.payment.none')}
        </span>
      );
    }

    const amount = amountCents ? (amountCents / 100).toFixed(2) : '0.00';
    const curr = currency?.toUpperCase() ?? 'DKK';

    switch (status) {
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-900/30 dark:text-green-200">
            <CheckCircle className="h-3 w-3" />
            {t('appointments.payment.paid')} ({amount} {curr})
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900/30 dark:text-red-200">
            <XCircle className="h-3 w-3" />
            {t('appointments.payment.failed')}
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
            <Clock className="h-3 w-3" />
            {t('appointments.payment.processing')}
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            <AlertCircle className="h-3 w-3" />
            {t('appointments.payment.pending')}
          </span>
        );
      case 'refunded':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800 dark:bg-gray-700 dark:text-gray-200">
            {t('appointments.payment.refunded')}
          </span>
        );
      default:
        return null;
    }
  };

  const getPaymentMethodDisplay = (method?: string | null) => {
    if (!method) return null;

    const methods: Record<string, { icon: typeof CreditCard; label: string }> = {
      card: { icon: CreditCard, label: t('appointments.payment.method_card') },
      mobilepay: { icon: CreditCard, label: t('appointments.payment.method_mobilepay') },
      punch_card: { icon: Ticket, label: t('appointments.payment.method_punch_card') },
      manual: { icon: Receipt, label: t('appointments.payment.method_manual') },
    };

    const methodInfo = methods[method];
    if (!methodInfo) return <span className="text-xs text-gray-500">{method}</span>;

    const Icon = methodInfo.icon;
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
        <Icon className="h-3 w-3" />
        {methodInfo.label}
      </span>
    );
  };

  const renderAppointmentCard = (apt: NonNullable<typeof appointments>[number]) => {
    const { date, time } = formatDateTime(apt.starts_at);

    return (
      <button
        key={apt.id}
        onClick={() => setEditingId(apt.id)}
        className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm transition-all hover:border-indigo-300 hover:shadow-md dark:border-gray-700 dark:bg-card-dark dark:hover:border-indigo-600"
      >
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Calendar className="h-4 w-4" />
              <span>{date}</span>
              <Clock className="h-4 w-4" />
              <span>{time}</span>
            </div>
            {getStatusBadge(apt.status)}
            {apt.invoice_id && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/invoices/${apt.invoice_id}`);
                }}
                className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-200 dark:hover:bg-indigo-900/50"
              >
                <Receipt className="h-3 w-3" />
                {apt.invoice_number
                  ? t('appointments.invoice_link', { number: apt.invoice_number })
                  : t('appointments.has_invoice')}
              </button>
            )}
          </div>

          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-1">
              <p className="font-medium text-gray-900 dark:text-white">
                {apt.service_name ?? t('appointments.no_service')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('appointments.practitioner')}: {apt.clinician_name ?? t('appointments.unassigned')}
              </p>
            </div>
            {apt.payment_status && (
              <div className="flex flex-col items-end gap-1">
                {getPaymentBadge(apt.payment_status, apt.payment_amount_cents, apt.payment_currency)}
                {apt.payment_method && getPaymentMethodDisplay(apt.payment_method)}
              </div>
            )}
          </div>

          {apt.notes && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {apt.notes.length > 100 ? `${apt.notes.substring(0, 100)}...` : apt.notes}
            </p>
          )}

          {apt.source && (
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {t('appointments.source')}: {apt.source}
            </p>
          )}
        </div>
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-200">
        {t('appointments.error_loading')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AppointmentStatus | 'all')}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="all">{t('appointments.filter.all')}</option>
            <option value="upcoming">{t('appointments.filter.upcoming')}</option>
            <option value="completed">{t('appointments.filter.completed')}</option>
            <option value="cancelled">{t('appointments.filter.cancelled')}</option>
            <option value="no_show">{t('appointments.filter.no_show')}</option>
          </select>
        </div>
        <Button onClick={() => setShowNewModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('appointments.new_appointment')}
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center dark:border-gray-700 dark:bg-gray-800">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {statusFilter === 'all'
              ? t('appointments.no_appointments')
              : t('appointments.no_appointments_filter')}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && (
            <div>
              <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                {t('appointments.upcoming_title')} ({upcoming.length})
              </h4>
              <div className="space-y-3">{upcoming.map(renderAppointmentCard)}</div>
            </div>
          )}

          {past.length > 0 && (
            <div>
              <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                {t('appointments.past_title')} ({past.length})
              </h4>
              <div className="space-y-3">{past.map(renderAppointmentCard)}</div>
            </div>
          )}
        </div>
      )}

      {showNewModal && (
        <AppointmentModal
          clinicId={clinicId}
          mode="create"
          initialPatientId={patientId}
          onClose={() => setShowNewModal(false)}
        />
      )}

      {editingId && (
        <AppointmentModal
          clinicId={clinicId}
          mode="edit"
          appointmentId={editingId}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}
